import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ProfilePhotoUpload } from '@adthlete/shared-frontend'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/sonner'
import { INPUT_CLASSES } from '@/lib/theme'
import type { SchoolOption } from '@/lib/api'
import SportsMultiSelect from '@/components/SportsMultiSelect'
import SchoolPicker from '@/components/SchoolPicker'
import { useAuth } from '@/hooks/useAuth'
import SetupShell, { SetupCard, SectionTitle } from '@/components/setup/SetupShell'
import { profilePieces } from '@/lib/athleteScore'
import { Check } from 'lucide-react'

const BIO_MAX = 600

/**
 * Photo, bio, sport, position and school: one screen with exactly those fields, opened from the
 * athlete's Content Shop and back there when saved.
 */
const AboutYou: React.FC = () => {
  const navigate = useNavigate()
  const { athlete, updateProfile, updateSports, updateSchool, updateAvatar, refreshUser } =
    useAuth()

  const [photo, setPhoto] = useState<string | undefined>(athlete?.avatar || undefined)
  const [bio, setBio] = useState(athlete?.bio ?? '')
  const [position, setPosition] = useState(athlete?.position ?? '')
  const [sportIds, setSportIds] = useState<number[]>(athlete?.sports ?? [])
  const [school, setSchool] = useState<SchoolOption | null>(
    athlete?.school && athlete.school_id
      ? { id: athlete.school_id, name: athlete.school.name }
      : null,
  )
  const [saving, setSaving] = useState(false)

  const handlePhotoChange = async (p: string) => {
    setPhoto(p)
    try {
      await updateAvatar(p)
    } catch {
      toast.error('Error', { description: 'Failed to update photo.' })
    }
  }

  const save = async () => {
    setSaving(true)
    try {
      await updateProfile({ bio: bio.trim(), position: position.trim() })
      if (JSON.stringify(sportIds) !== JSON.stringify(athlete?.sports ?? [])) {
        await updateSports(sportIds)
      }
      if (school && school.id !== athlete?.school_id) await updateSchool(school.id)
      await refreshUser()
      toast('Saved', { description: 'Brands can see who you are now.' })
      navigate('/brand-view')
    } catch (err: any) {
      toast.error('Could not save', { description: err?.response?.data?.error ?? err?.message })
    } finally {
      setSaving(false)
    }
  }

  const remaining = BIO_MAX - bio.length
  // Live checklist: what is already saved plus what is typed but not saved yet
  const pieces = profilePieces({
    avatar: photo,
    bio: bio.trim(),
    sports: sportIds,
    school: school ? { id: String(school.id), name: school.name } : null,
  })
  const doneCount = pieces.filter((p) => p.done).length

  return (
    <SetupShell
      title="About you"
      subtitle={
        <div>
          <SectionTitle>How brands see you</SectionTitle>
          <p className="mt-1 text-sm text-gray-600">
            A photo, a line about you, and where you play. This is what shows on offers.
          </p>
        </div>
      }
      footer={
        <Button variant="lime" className="h-12 w-full text-base" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save and back to your Content Shop'}
        </Button>
      }
      footnote="Sport and school match you with local brands."
    >
      <ul className="grid grid-cols-4 gap-2">
        {pieces.map((p) => (
          <li
            key={p.id}
            className={`flex items-center justify-center gap-1.5 rounded-xl border py-2 text-xs font-semibold ${
              p.done
                ? 'border-[#C0FD72]/60 bg-[#C0FD72]/10 text-[#4d7c0f]'
                : 'border-foreground/10 text-gray-600'
            }`}
          >
            {p.done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
            {p.label}
          </li>
        ))}
      </ul>
      <p className="px-1 text-xs text-gray-500">{doneCount} of 4 done</p>
      <SetupCard>
        <ProfilePhotoUpload
          currentPhoto={photo}
          onPhotoChange={handlePhotoChange}
          onSuccess={(msg) => toast('Photo saved', { description: msg })}
          onError={(err) => toast.error('Error', { description: err.message })}
        />
      </SetupCard>

      <SetupCard className="space-y-2">
        <div className="flex items-baseline justify-between">
          <Label htmlFor="bio" className="font-bold text-foreground">
            Bio
          </Label>
          <span className={`text-xs ${remaining < 40 ? 'text-orange-700' : 'text-gray-500'}`}>
            {remaining}
          </span>
        </div>
        <Textarea
          id="bio"
          value={bio}
          maxLength={BIO_MAX}
          onChange={(e) => setBio(e.target.value)}
          placeholder="Two or three lines. What you play, what you post, what you're into."
          className={`${INPUT_CLASSES.dark} min-h-24 resize-none`}
        />
      </SetupCard>

      <SetupCard className="space-y-4">
        <SportsMultiSelect
          label="Sports"
          description="Add every sport you compete in"
          selectedSportIds={sportIds}
          onSportsChange={setSportIds}
          placeholder="Add a sport…"
        />
        <div className="space-y-1.5">
          <Label htmlFor="position" className="text-foreground">
            Position <span className="font-normal text-gray-500">(optional)</span>
          </Label>
          <Input
            id="position"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            placeholder="e.g. Outside hitter, Linebacker, 200m freestyle"
            className={INPUT_CLASSES.dark}
            maxLength={80}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="school" className="text-foreground">
            School
          </Label>
          <SchoolPicker id="school" value={school} onChange={setSchool} />
        </div>
      </SetupCard>
    </SetupShell>
  )
}

export default AboutYou
